/* eslint-disable no-empty-pattern */
import type { ApolloClient } from '@apollo/client/core';
import * as Sentry from '@sentry/react';
import type { Moment } from 'moment';

import type { ActionContext } from 'js/lib/ActionContext';

import SessionAPIUtils from 'bundles/author-course/utils/CourseSessionAPIUtils';
import {
  getPostSessionItemsVisibilityOverrides,
  savePostSessionItemsVisibility,
} from 'bundles/author-sessions/components/sessionItem/editModal/lib/sessionItemsVisibilityUtils';
import { toJson } from 'bundles/author-sessions/lib/session/transforms';
import getErrorMessagesForSessions from 'bundles/author-sessions/utils/getErrorMessagesForSessions';
import { setServerErrorMessage } from 'bundles/phoenix/components/ConnectedInlineMessage';

type updateSessionItemDeadlineMomentPayload = {
  sessionId: string;
  itemId: string;
  deadlineMoment: Moment;
};

type updateSessionItemSchedulePayload = {
  sessionId: string;
  itemId: string;
  removeTimedItemLock: boolean;
  unlockTime: number | undefined;
  relockTime: number | undefined;
  timedItemLockCustomMessage: string | undefined;
};

export type updateSessionItemsVisibilityPayload = {
  sessionId: string;
  items: Array<{
    itemId: string;
    atomId: string;
    visibility: boolean;
  }>;
  itemVisibility: boolean;
};

export type saveSessionItemsVisibilityPayload = {
  client: ApolloClient<unknown>;
  sessionId: string;
  limit: number;
  items: Array<{
    itemId: string;
    atomId: string;
    visibility: boolean;
  }>;
  messageKey: string;
  skip?: boolean;
  successCallback?: () => void;
};

// Server Actions
export const loadSessionsByCourseId = (actionContext: $TSFixMe, { courseId }: $TSFixMe, done: $TSFixMe) => {
  const promise = SessionAPIUtils.listSessions(courseId)
    .then((response: $TSFixMe) => {
      actionContext.dispatch('RECEIVED_SESSIONS', response.elements);
    })
    .catch((err: $TSFixMe) => {
      console.error(`Error loading sessions by courseId(${courseId})`, err); // eslint-disable-line
    });

  promise.done();
  return promise;
};

export const loadSessionById = (actionContext: $TSFixMe, { sessionId }: $TSFixMe, done: $TSFixMe) => {
  const promise = SessionAPIUtils.getSession(sessionId)
    .then((response: $TSFixMe) => {
      actionContext.dispatch('RECEIVED_SESSIONS', response.elements);
    })
    .catch((err: $TSFixMe) => {
      console.error(`Error loading session by sessionId(${sessionId})`, err); // eslint-disable-line
    });

  promise.done();
  return promise;
};

export const loadSessionItemsVisibilityOverrides = function (
  actionContext: ActionContext,
  { client, sessionId, limit }: { client: ApolloClient<unknown>; sessionId: string; limit: number }
) {
  return getPostSessionItemsVisibilityOverrides(client, sessionId, limit)
    .then((response) => {
      actionContext.dispatch('RECEIVED_SESSION_ITEMS_VISIBILITY_OVERRIDES', {
        sessionId,
        items: response,
      });
    })
    .catch((err) => {
      Sentry.captureException(err);
      console.error(`Error loading session items vibility overrides by sessionId(${sessionId})`, err); // eslint-disable-line
    });
};

// Client Actions
export const saveSessionToServer = (
  actionContext: $TSFixMe,
  { sessionId, session, messageKey, successCallback }: $TSFixMe
) => {
  const updatedSession = Object.assign(session, {
    itemDeadlinesOverrides: session.itemDeadlines,
  });
  const promise = SessionAPIUtils.updateSession(sessionId, toJson(updatedSession))
    .then((response: $TSFixMe) => {
      actionContext.dispatch('SAVED_SESSION_TO_SERVER', {
        sessionId,
        sessionData: response.elements ? response.elements[0] : updatedSession,
      });
      successCallback?.();
    })
    .catch((err: $TSFixMe) => {
      actionContext.executeAction(setServerErrorMessage, {
        messageKey,
        contents: getErrorMessagesForSessions(err),
      });
      actionContext.dispatch('FAILED_TO_SAVE_SESSION_TO_SERVER', {});
    });

  promise.done();
  return promise;
};

export const saveUncommittedSession = (
  actionContext: $TSFixMe,
  { courseId, startedAt, enrollmentEndedAt, endedAt, isPrivate, messageKey }: $TSFixMe,
  done: $TSFixMe
) => {
  const promise = SessionAPIUtils.createSession(
    courseId,
    startedAt ? startedAt.valueOf() : null,
    enrollmentEndedAt ? enrollmentEndedAt.valueOf() : null,
    endedAt ? endedAt.valueOf() : null,
    isPrivate
  )
    .then(
      (response: $TSFixMe) => {
        actionContext.dispatch('SAVED_UNCOMMITTED_SESSION', response.elements[0]);
      },
      (err: $TSFixMe) => {
        actionContext.executeAction(setServerErrorMessage, {
          messageKey,
          contents: err,
        });
        actionContext.dispatch('FAILED_TO_SAVE_UNCOMMITTED_SESSION');
      }
    )
    .then(() => SessionAPIUtils.listSessions(courseId))
    .then((response: $TSFixMe) => {
      actionContext.dispatch('RECEIVED_SESSIONS', response.elements);
    });

  promise.done();
  return promise;
};

export const deleteSessionOnServer = (actionContext: $TSFixMe, { sessionId, messageKey }: $TSFixMe, done: $TSFixMe) => {
  const promise = SessionAPIUtils.deleteSession(sessionId)
    .then(() => {
      actionContext.dispatch('DELETE_SESSION_SUCCESS', sessionId);
    })
    .catch((err: $TSFixMe) => {
      actionContext.executeAction(setServerErrorMessage, {
        messageKey,
        contents: err,
      });
      actionContext.dispatch('DELETE_SESSION_FAILURE', sessionId);
    })
    .then(done);

  promise.done();
  return promise;
};

export const saveSessionItemsVisibilityOverridesToServer = function (
  actionContext: ActionContext,
  { client, sessionId, items, messageKey, successCallback, skip = false }: saveSessionItemsVisibilityPayload
) {
  if (!skip && items.length > 0) {
    return savePostSessionItemsVisibility(client, sessionId, items)
      .then((response) => {
        actionContext.dispatch('RECEIVED_SESSION_ITEMS_VISIBILITY_OVERRIDES', {
          sessionId,
          items: response,
        });
        successCallback?.();
      })
      .catch((err) => {
        Sentry.captureException(err);
        actionContext.executeAction(setServerErrorMessage, {
          messageKey,
          contents: 'Failed to save post-session visibility settings',
        });
        actionContext.dispatch('FAILED_TO_SAVE_SESSION_TO_SERVER', {});
      });
  }
  successCallback?.();
  return Promise.resolve();
};

export const updateSessionModuleDeadlineMoment = (
  actionContext: $TSFixMe,
  { sessionId, moduleIds, deadlineMoment }: $TSFixMe,
  done: $TSFixMe
) => {
  actionContext.dispatch('UPDATE_SESSION_MODULE_DEADLINE_MOMENT', {
    sessionId,
    moduleIds,
    deadlineMoment,
  });
};

export const updateSessionItemDeadlineMoment = function (
  actionContext: ActionContext,
  payload: updateSessionItemDeadlineMomentPayload
) {
  actionContext.dispatch('UPDATE_SESSION_ITEM_DEADLINE_MOMENT', payload);
};

export const updateSessionItemSchedule = function (
  actionContext: ActionContext,
  payload: updateSessionItemSchedulePayload
) {
  actionContext.dispatch('UPDATE_SESSION_ITEM_SCHEDULE', payload);
};

export const updateSessionItemsVisibility = function (
  actionContext: ActionContext,
  payload: updateSessionItemsVisibilityPayload
) {
  actionContext.dispatch('UPDATE_SESSION_ITEMS_VISIBILITY_OVERRIDES', payload);
};

export const deleteSessionItemDeadlineMoment = (
  actionContext: $TSFixMe,
  { sessionId, itemId }: $TSFixMe,
  done: $TSFixMe
) => {
  actionContext.dispatch('DELETE_SESSION_ITEM_DEADLINE_MOMENT', {
    sessionId,
    itemId,
  });
};

export const updateSessionDateMoments = (
  actionContext: $TSFixMe,
  { sessionId, endedAt, startedAt, enrollmentEndedAt, items }: $TSFixMe,
  done: $TSFixMe
) => {
  actionContext.dispatch('UPDATE_SESSION_DATE_MOMENTS', {
    sessionId,
    endedAt,
    startedAt,
    enrollmentEndedAt,
    items,
  });
};

export const createUncommittedSession = (actionContext: $TSFixMe, {}) => {
  actionContext.dispatch('CREATE_UNCOMMITTED_SESSION');
};

export const cancelCreateUncommittedSession = (actionContext: $TSFixMe, {}) => {
  actionContext.dispatch('CANCEL_CREATE_UNCOMMITTED_SESSION');
};

export const updateUncommittedSession = (actionContext: $TSFixMe, options: $TSFixMe, done: $TSFixMe) => {
  actionContext.dispatch('UPDATE_UNCOMMITTED_SESSION', options);
};

export const resetSessionModifications = (actionContext: $TSFixMe, { sessionId, itemId }: $TSFixMe, done: $TSFixMe) => {
  actionContext.dispatch('RESET_SESSION_MODIFICATIONS', { sessionId, itemId });
};

export const clearAllSessions = (actionContext: $TSFixMe, {}) => {
  actionContext.dispatch('CLEAR_ALL_SESSIONS');
};
